/**
 * Набор инлайновых иконок (§2.3, §13).
 *
 * Правила, одинаковые для всех: сетка 20×20, рендер 16 px (в таблице) или
 * 20 px (в хроме), `currentColor`, обводка 1.5 px, скруглённые концы.
 * Иконка по умолчанию декоративная (`aria-hidden`) — смысл несёт текст рядом.
 * Если иконка стоит одна и несёт смысл, ей передаётся `title`: тогда она
 * получает `role="img"` и доступное имя (§13, «цвет не единственный носитель»).
 */
import type { ReactNode, SVGProps } from 'react'
import './icons.css'

/** Разрешены только два размера: 16 внутри строки таблицы, 20 в хроме. */
export type IconSize = 16 | 20

export interface IconProps
  extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'viewBox' | 'children'> {
  size?: IconSize
  /** Доступное имя. Без него иконка скрыта от скринридера как декоративная. */
  title?: string
}

function Icon({
  size = 16,
  title,
  className,
  children,
  ...rest
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  )
}

/* ─────────────────────────── шевроны и стрелки ─────────────────────────── */

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 8 5 5 5-5" />
  </Icon>
)

export const IconChevronUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12 5-5 5 5" />
  </Icon>
)

export const IconChevronLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="m12 5-5 5 5 5" />
  </Icon>
)

export const IconChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="m8 5 5 5-5 5" />
  </Icon>
)

export const IconArrowUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 16V4" />
    <path d="m5.5 8.5 4.5-4.5 4.5 4.5" />
  </Icon>
)

export const IconArrowDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 4v12" />
    <path d="m5.5 11.5 4.5 4.5 4.5-4.5" />
  </Icon>
)

export const IconArrowLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="M16 10H4" />
    <path d="m8.5 5.5-4.5 4.5 4.5 4.5" />
  </Icon>
)

export const IconArrowRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 10h12" />
    <path d="m11.5 5.5 4.5 4.5-4.5 4.5" />
  </Icon>
)

/* ─────────────────────────── базовые действия ─────────────────────────── */

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 5.5l9 9" />
    <path d="M14.5 5.5l-9 9" />
  </Icon>
)

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m4.5 10.5 3.8 3.8L15.5 7" />
  </Icon>
)

export const IconPlus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 4.5v11" />
    <path d="M4.5 10h11" />
  </Icon>
)

export const IconTrash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 6.5h12" />
    <path d="M8 6.5V4.8c0-.4.3-.8.8-.8h2.4c.5 0 .8.4.8.8v1.7" />
    <path d="m5.8 6.5.8 9.2c0 .7.6 1.3 1.3 1.3h4.2c.7 0 1.3-.6 1.3-1.3l.8-9.2" />
  </Icon>
)

export const IconSparkle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 2.6 11.7 7l4.4 1.7-4.4 1.7L10 14.8 8.3 10.4 3.9 8.7 8.3 7z" />
    <path d="M15.6 13.2 16.3 15l1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
  </Icon>
)

/* ─────────────────────────────── тема ─────────────────────────────────── */

export const IconSun = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="3.6" />
    <path d="M10 2.6v1.8M10 15.6v1.8M17.4 10h-1.8M4.4 10H2.6M15.2 4.8l-1.3 1.3M6.1 13.9l-1.3 1.3M15.2 15.2l-1.3-1.3M6.1 6.1 4.8 4.8" />
  </Icon>
)

export const IconMoon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M16.2 11.7A6.8 6.8 0 0 1 8.3 3.8a6.8 6.8 0 1 0 7.9 7.9z" />
  </Icon>
)

export const IconDisplay = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.8" y="4" width="14.4" height="9.4" rx="1.5" />
    <path d="M7 16.6h6" />
  </Icon>
)

/* ─────────────────────────── таблица и фильтры ─────────────────────────── */

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="9" r="5" />
    <path d="m12.8 12.8 3.7 3.7" />
  </Icon>
)

export const IconFilter = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.5 5h13l-5 6v5.2l-3-1.6V11z" />
  </Icon>
)

export const IconColumns = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4" width="13" height="12" rx="1.5" />
    <path d="M7.8 4v12" />
    <path d="M12.2 4v12" />
  </Icon>
)

export const IconSort = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 4.5v11" />
    <path d="m4 13 2.5 2.5L9 13" />
    <path d="M13.5 15.5v-11" />
    <path d="m11 7 2.5-2.5L16 7" />
  </Icon>
)

/* ─────────────────────────── данные и история ─────────────────────────── */

export const IconComment = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 6.2C4 5 5 4 6.2 4h7.6C15 4 16 5 16 6.2v4.6c0 1.2-1 2.2-2.2 2.2H8.4L4.6 16v-3.2z" />
  </Icon>
)

export const IconHistory = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.6 10a6.4 6.4 0 1 0 1.9-4.5" />
    <path d="M3.4 3.6v2.6h2.6" />
    <path d="M10 6.6V10l2.6 1.8" />
  </Icon>
)

export const IconLink = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.6 6.6H6.4a3.4 3.4 0 0 0 0 6.8h2.2" />
    <path d="M11.4 13.4h2.2a3.4 3.4 0 0 0 0-6.8h-2.2" />
    <path d="M7 10h6" />
  </Icon>
)

export const IconExternalLink = (p: IconProps) => (
  <Icon {...p}>
    <path d="M11.5 4H16v4.5" />
    <path d="M16 4 9.8 10.2" />
    <path d="M13.8 11.8v2.9c0 .7-.6 1.3-1.3 1.3h-7c-.7 0-1.3-.6-1.3-1.3v-7c0-.7.6-1.3 1.3-1.3h2.9" />
  </Icon>
)

export const IconCopy = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="7.5" width="9" height="9" rx="1.5" />
    <path d="M7.5 7.5V5c0-.8.7-1.5 1.5-1.5h6.5c.8 0 1.5.7 1.5 1.5v6.5c0 .8-.7 1.5-1.5 1.5H13" />
  </Icon>
)

/* ─────────────────────────── состояния и предупреждения ───────────────── */

export const IconEye = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.6 10S5.6 5.2 10 5.2 17.4 10 17.4 10 14.4 14.8 10 14.8 2.6 10 2.6 10z" />
    <circle cx="10" cy="10" r="2.2" />
  </Icon>
)

export const IconEyeOff = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.1 6.6C3.5 7.9 2.6 10 2.6 10S5.6 14.8 10 14.8c1.2 0 2.3-.3 3.2-.9" />
    <path d="M15.4 12.6c1.3-1.3 2-2.6 2-2.6S14.4 5.2 10 5.2c-.7 0-1.3.1-1.9.3" />
    <path d="M8.4 8.4a2.2 2.2 0 0 0 3.2 3.2" />
    <path d="M4.5 15.5 15.5 4.5" />
  </Icon>
)

/** Запрет: видимость «Нет» (§4.3, символ ⊘). */
export const IconProhibited = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="6.4" />
    <path d="M5.5 14.5 14.5 5.5" />
  </Icon>
)

/** Предупреждение: контурный треугольник. Не красный по умолчанию — цвет даёт место. */
export const IconWarning = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 4.2 17 16.4H3z" />
    <path d="M10 8.6v3.4" />
    <path d="M10 14.3h.01" />
  </Icon>
)

/** Конфликт: залитый треугольник (§12.5, «▲ 1 конфликт»). Цвет — `--danger`. */
export const IconConflict = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 4.2 17 16.4H3z" fill="currentColor" stroke="none" />
  </Icon>
)

/** Правка не записалась (§11.3): перечёркнутое облако. */
export const IconCloudOff = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.6 14.4H6.2a2.9 2.9 0 0 1-.4-5.8 4 4 0 0 1 6.6-2.2" />
    <path d="M14.4 7.8a2.9 2.9 0 0 1 1.9 4.9" />
    <path d="M4.5 4.5 15.5 15.5" />
  </Icon>
)

/** Точка-индикатор: черновик у имени бренда, непрочитанное, «обновления не идут». */
export const IconDot = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="3.2" fill="currentColor" stroke="none" />
  </Icon>
)

/* ─────────── иконки разделов левого меню ───────────
   Раньше разделы делили иконки с инструментами таблицы: «Пользователи» были
   облачком комментария, «Настройки» — колонками. По такому меню нельзя было
   ориентироваться, не читая подписи. */

/** Сводка изменений с прошлого визита: лоток входящих. */
export const IconInbox = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 11.5 5.2 4.6A1.5 1.5 0 0 1 6.6 3.6h6.8a1.5 1.5 0 0 1 1.4 1L17 11.5" />
    <path d="M3 11.5h3.4l1 2.2h5.2l1-2.2H17v3.4a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.9z" />
  </Icon>
)

/** Справочники: книга. */
export const IconBook = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 4.2h4.3A2.2 2.2 0 0 1 10 5.9v10a2 2 0 0 0-1.7-1H4z" />
    <path d="M16 4.2h-4.3A2.2 2.2 0 0 0 10 5.9v10a2 2 0 0 1 1.7-1H16z" />
  </Icon>
)

/** Конструктор полей: карточка со строками. */
export const IconFields = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.2" y="4.2" width="13.6" height="11.6" rx="2" />
    <path d="M6.4 8.2h7.2M6.4 11.8h4.4" />
  </Icon>
)

/** Пользователи: двое. */
export const IconUsers = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="7.6" r="2.8" />
    <path d="M2.8 16.2a5.2 5.2 0 0 1 10.4 0" />
    <path d="M13.4 5.2a2.6 2.6 0 0 1 0 5" />
    <path d="M14.6 11.6a4.6 4.6 0 0 1 2.6 4.6" />
  </Icon>
)

/**
 * Настройки: шестерёнка.
 *
 * Зубцы — часть контура, а не лучи вокруг круга: прежняя версия рисовала
 * окружность с восемью отрезками наружу и читалась как солнце.
 */
export const IconSettings = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.13 1.91L11.87 1.91L12.09 4.27L12.58 4.47L14.4 2.96L17.04 5.6L15.53 7.42L15.73 7.91L18.09 8.13L18.09 11.87L15.73 12.09L15.53 12.58L17.04 14.4L14.4 17.04L12.58 15.53L12.09 15.73L11.87 18.09L8.13 18.09L7.91 15.73L7.42 15.53L5.6 17.04L2.96 14.4L4.47 12.58L4.27 12.09L1.91 11.87L1.91 8.13L4.27 7.91L4.47 7.42L2.96 5.6L5.6 2.96L7.42 4.47L7.91 4.27Z" />
    <circle cx="10" cy="10" r="2.5" />
  </Icon>
)

/** Отправка сообщения: бумажный самолётик. */
export const IconSend = (p: IconProps) => (
  <Icon {...p}>
    <path d="M17.2 10 3.4 4.2l2.3 5.8-2.3 5.8z" />
    <path d="M5.7 10h11.5" />
  </Icon>
)

/** Прикрепить файл: скрепка. */
export const IconPaperclip = (p: IconProps) => (
  <Icon {...p}>
    <path d="M14.8 9.3 9.6 14.5a3.1 3.1 0 0 1-4.4-4.4l5.9-5.9a2.1 2.1 0 0 1 2.9 2.9l-5.8 5.9a1 1 0 0 1-1.5-1.5l5.2-5.2" />
  </Icon>
)
